import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Filters from '../../components/Filters';
import FilterPills from '../../components/FilterPills';
import TradeGrid from '../../components/TradeGrid';
import ROUTES from '../../components/Routes/constants';
import TradeFilterBar from '../../components/TradeFilterBar';
import { updateSelectedTrade } from '../../redux/slices/ui/selectedTrades';
import { setFilterCriteria } from '../../redux/slices/filters';
import useTradeList from '../../_shared/hooks/trade/useTradeList';
import {
  FilterNames,
  ListVariant,
  TradeStateName,
  TradeState
} from '../../_shared/types/trade';
import MixpanelService from '../../services/MixpanelService';
import MixpanelConfig from '../../_shared/constants/mixpanelConfig';
import useFilterList from '../../_shared/hooks/list/useFilterList';
import useTradeSort from '../../_shared/hooks/trade/sort/useTradeSort';
import { BidConfirmationModalType } from '../../_shared/types/auction';
import { objectToQueryString } from '../../_shared/utils/url';
import convertToCurrency from '../../_shared/utils/convertToCurrency';
import Pagination from '../../components/Pagination';
import ConfirmModal from '../../_shared/components/ConfirmModal';
import BidModal from '../../components/BidModal';
import { localStorageService } from '../../services/StorageService';
import LOCALSTORAGE from '../../_shared/constants/localStorage';
import MiniVdp from '../../components/MiniVdp';
import { TradeSortOptionsByTime } from '../../_shared/hooks/trade/sort/constants/tradeSortMethods';
import useUserPreferences from '../../_shared/hooks/user/useUserPreferences';
import TradeListFooter from '../../components/TradeList/TradeListFooter';
import useCurrentTradeState from '../../_shared/hooks/trade/useCurrentTradeState';
import TradeListHeader from '../../components/TradeList/TradeListHeader';
import NavSecondary from '../../components/NavSecondary';
import tradeListRefresh from '../../_shared/constants/tradeListRefresh';
import {
  StyledFilterIndicator,
  StyledHeader,
  StyledPage,
  StyledTradeGridContainer,
  StyledTradesContainer,
  StyledRefreshButtonContainer,
  StyledRefreshButton
} from './Styles';
import ErrorService from '../../services/ErrorService';

const Buying = ({ className }) => {
  const history = useHistory();
  const tradeState = useCurrentTradeState();
  const [
    context,
    listVariant,
    activeList,
    previewCriteria,
    activeNetwork,
    user,
    selectedSortOption,
    sseConnectionStatus
  ] = useSelector(
    state => [
      state?.ui?.nav?.primary?.context,
      state?.ui?.layout?.listVariant,
      state?.filter?.activeFilter,
      state?.filter?.previewCriteria,
      state?.filter?.activeNetwork?.[tradeState],
      state?.auth?.user,
      state?.ui?.tradeSort?.[tradeState],
      state?.sseConnectionStatus
    ],
    shallowEqual
  );
  const dispatch = useDispatch();
  const intl = useIntl();
  const { search } = useLocation();
  const mixpanel = MixpanelService.get();
  const {
    data: userLists,
    isLoading,
    refetch,
    isFetched: isFiltersListFetched
  } = useFilterList(tradeState);
  useUserPreferences();

  const filtersButtonRef = useRef(null);
  const bidModalRef = useRef({});
  const tradeGridRef = useRef(null);
  const searchString = window.location.search;
  const params = new URLSearchParams(searchString);
  const pageNumber = Number(params?.get('page')) || 1;
  const scrollToTradeId =
    localStorageService.getItem(LOCALSTORAGE.BUY_TRADE) || undefined;

  const setSortOption = useTradeSort();
  const [numberOfTrades, setNumberOfTrades] = useState(0);

  const showSaleEvent =
    selectedSortOption &&
    TradeSortOptionsByTime.includes(selectedSortOption) &&
    activeList?.id === FilterNames.all.id &&
    !previewCriteria?.criteria;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
    isFetching,
    refetch: refetchTradeList
  } = useTradeList(
    {
      context,
      tradeState,
      pagination: {
        sortMethod: selectedSortOption
      },
      listId: activeList?.id
    },
    Number(pageNumber) - 1,
    previewCriteria,
    activeNetwork,
    listVariant,
    showSaleEvent
  );

  const [resultsLoading, setResultsLoading] = useState(true);
  const [confirmationMpTracking, setConfirmationMpTracking] = useState({});
  const [filterPillName, setFilterPillName] = useState('');

  const [tradeListItems, setTradeListItems] = useState([]);
  const [gridTradeListItems, setGridTradeListItems] = useState([]);
  const [saleEventItems, setSaleEventItems] = useState([]);
  const [loadingPrevPage, setLoadingPrevPage] = useState(
    isFetchingPreviousPage
  );
  const [pendingActiveProxy, setPendingActiveProxy] = useState(null);
  const [isEditingFilters, setIsEditingFilters] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmModalLogic, setConfirmModalLogic] = useState([]);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const [isFilterReset, setIsFilterReset] = useState(false);

  const [refreshButton, setRefreshButton] = useState(false);

  const updateQueryParam = page => {
    const urlParams = new URLSearchParams(search);
    const newQuery = {
      ...Object.fromEntries(urlParams),
      listId: activeList.id,
      page
    };
    return { search: objectToQueryString(newQuery) };
  };

  const getNextPage = () => {
    setLoadingPrevPage(false);
    fetchNextPage();
    const page = Number(pageNumber) + 1;
    history.push(updateQueryParam(page));
    window.scrollTo(0, 0);
  };

  const getPrevPage = () => {
    fetchPreviousPage();
    setLoadingPrevPage(true);
    const page = Number(pageNumber) - 1;
    history.push(updateQueryParam(page));
    window.scrollTo(0, 0);
  };

  // update the filter criteria
  const filterCriteria = data?.pages[0]?.filterCriteria;
  useEffect(() => {
    // update the redux store with the filter criteria when
    // the id of the filter criteria returned from the api changes
    if (filterCriteria) {
      dispatch(setFilterCriteria(filterCriteria));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterCriteria?.id]);

  const isLoadingResults = () =>
    isFetchingNextPage ||
    isFetchingPreviousPage ||
    isLoading ||
    isFetching ||
    resultsLoading;

  const [shouldOpenConfirmModal, setShouldOpenConfirmModal] = useState(false);

  const { setBidContext } = bidModalRef?.current;

  const onConfirmOpen = (
    confirmationTypes,
    setTrackShowConfirmModal,
    onConfirmCallback,
    onCancelCallback,
    amount
  ) => {
    const closeConfirmModal = () => {
      setConfirmModalLogic([]);
      onCancelCallback();
      setIsConfirmationOpen(false);
    };

    const confirmBid = async () => {
      setIsConfirmLoading(true);
      await onConfirmCallback();
      setIsConfirmLoading(false);
      setConfirmationMpTracking({});
      closeConfirmModal();
    };

    // TODO: move this out into a separate file, potentially too much logic in the Buying page
    if (confirmationTypes?.length) {
      confirmationTypes?.forEach((type, index) => {
        switch (type) {
          case BidConfirmationModalType.legal:
            setConfirmModalLogic(modal =>
              modal.concat([
                {
                  title: (
                    <FormattedMessage id="modal.confirmation.confirmYourBid.title" />
                  ),
                  msg: (
                    <FormattedMessage id="modal.confirmation.confirmYourBid.description" />
                  ),
                  onConfirm: () => () => {
                    setBidContext(bidContext => ({
                      ...bidContext,
                      confirmation: true
                    }));
                    setTrackShowConfirmModal(true);
                    if (index + 1 === confirmationTypes.length) confirmBid();
                  },
                  confirmLabel: (
                    <FormattedMessage id="vdp.confirmation.yesBid" />
                  ),
                  onCancel: () => () => {
                    setTrackShowConfirmModal(true);
                    closeConfirmModal();
                  },
                  cancelLabel: <FormattedMessage id="modal.cancel" />
                }
              ])
            );
            break;
          case BidConfirmationModalType.overBidThreshold:
            setConfirmModalLogic(modal =>
              modal.concat([
                {
                  title: (
                    <FormattedMessage
                      id="vdp.confirmation.title.bidSeemsHigh"
                      values={{ bidAmount: convertToCurrency(amount) }}
                    />
                  ),
                  msg: (
                    <FormattedMessage
                      id="vdp.confirmation.msg.confirmBidAmount"
                      values={{ bidAmount: convertToCurrency(amount) }}
                    />
                  ),
                  onConfirm: () => () => {
                    setBidContext(bidContext => ({
                      ...bidContext,
                      highBid: true
                    }));

                    if (index + 1 === confirmationTypes.length) confirmBid();
                  },
                  confirmLabel: (
                    <FormattedMessage id="vdp.confirmation.yesBid" />
                  ),
                  onCancel: () => closeConfirmModal,
                  cancelLabel: <FormattedMessage id="modal.cancel" />
                }
              ])
            );
            break;
          case BidConfirmationModalType.outbidDGN:
            setConfirmModalLogic(modal =>
              modal.concat([
                {
                  title: (
                    <FormattedMessage id="vdp.confirmation.title.sameDealerGroup" />
                  ),
                  msg: <FormattedMessage id="vdp.confirmation.msg.submitBid" />,
                  onConfirm: () => () => {
                    setBidContext(bidContext => ({
                      ...bidContext,
                      outbidDealerGroup: true
                    }));

                    if (index + 1 === confirmationTypes.length) confirmBid();
                  },
                  confirmLabel: (
                    <FormattedMessage id="vdp.confirmation.yesBid" />
                  ),
                  onCancel: () => closeConfirmModal,
                  cancelLabel: <FormattedMessage id="modal.cancel" />
                }
              ])
            );
            break;
          case BidConfirmationModalType.outbidDealership:
            setConfirmModalLogic(modal =>
              modal.concat([
                {
                  title: (
                    <FormattedMessage id="vdp.confirmation.title.sameDealership" />
                  ),
                  msg: (
                    <FormattedMessage id="vdp.confirmation.title.sameDealershipMessage" />
                  ),
                  onConfirm: () => () => {
                    setBidContext(bidContext => ({
                      ...bidContext,
                      outbidDealership: true
                    }));

                    if (index + 1 === confirmationTypes.length) confirmBid();
                  },
                  confirmLabel: (
                    <FormattedMessage id="vdp.confirmation.yesBid" />
                  ),
                  onCancel: () => closeConfirmModal,
                  cancelLabel: <FormattedMessage id="modal.cancel" />
                }
              ])
            );
            break;
          default:
            break;
        }
      });
    } else {
      confirmBid();
    }

    setShouldOpenConfirmModal(true);
  };

  useEffect(() => {
    if (!sseConnectionStatus.isConnected) {
      const error = new Error(
        'Failed to fetch (SSE connection failed)',
        sseConnectionStatus.error
      );
      ErrorService.trackError(error);
    }
  }, [sseConnectionStatus]);

  useEffect(() => {
    if (confirmModalLogic?.length && shouldOpenConfirmModal) {
      setIsConfirmationOpen(true);
    }
  }, [confirmModalLogic?.length, shouldOpenConfirmModal]);

  const onCurrentProxyUpdated = ({ tradeId, bidAmount, proxyAmount }) => {
    const liveTradeIndex = tradeListItems.findIndex(
      tradeItem => tradeItem.id === tradeId
    );

    if (liveTradeIndex >= 0) {
      setPendingActiveProxy({ tradeId, bidAmount, proxyAmount });
    }
  };

  useEffect(() => {
    if (data?.pages && data?.pages[0]) {
      setTradeListItems(data.pages[0]?.tradeSummaries);
      setGridTradeListItems(data.pages[0]?.gridTradeSummaries);
      setSaleEventItems(data.pages[0]?.saleEventSummaries);
      setNumberOfTrades(
        data.pages[0].tradeCount || data.pages[0].tradeSummaries.length
      );
      setResultsLoading(false);
    }
  }, [data?.pages, pageNumber, loadingPrevPage]);

  const tradeSelectionHandler = tradeId => {
    localStorageService.setItem(LOCALSTORAGE.BUY_TRADE, tradeId);
    dispatch(
      updateSelectedTrade({
        tradeState,
        tradeId
      })
    );

    if (listVariant === ListVariant.LIST) {
      <Redirect
        to={{
          pathname: `${ROUTES.TRADE_URI}${tradeId}`
        }}
      />;
      window.scrollTo(0, 0);
    }
  };

  const filterCountSubtitle = filterId => {
    let formattedMessage;

    if (filterId === FilterNames.all.id) {
      formattedMessage = intl.formatMessage({
        id: 'list.filter.matchingAll'
      });
    } else {
      const message = intl.formatMessage({
        id: 'list.filter.matching'
      });

      formattedMessage = `${message}`;
    }

    return formattedMessage;
  };

  useEffect(() => {
    setTimeout(() => {
      if (
        scrollToTradeId &&
        !isFetchingNextPage &&
        !isFetchingPreviousPage &&
        !isLoading &&
        !isFetching &&
        !resultsLoading
      ) {
        const tradeEle = document.getElementById(
          `tradeCard-${scrollToTradeId}`
        );
        if (tradeEle) {
          tradeEle.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }
        localStorageService.removeItem(LOCALSTORAGE.BUY_TRADE);
      }
    }, 1000);
  }, [
    scrollToTradeId,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isLoading,
    isFetching,
    resultsLoading
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        !isFetchingNextPage &&
        !isFetchingPreviousPage &&
        !isLoading &&
        !isFetching &&
        !resultsLoading &&
        process.env.REACT_APP_SHOW_LIST_REFETCH
      ) {
        const auctionEndedTradeCards = document.querySelectorAll(
          '[data-testid*=auction-ended]'
        ).length;
        if (auctionEndedTradeCards >= tradeListRefresh.MIN_INVALID_TRADES) {
          setRefreshButton(true);
        }
      }
    }, tradeListRefresh.CHECK_LIST_INTERVAL);

    return () => clearInterval(interval);
  }, [
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isLoading,
    resultsLoading
  ]);

  useEffect(() => {
    if (tradeState && process.env.REACT_APP_SHOW_LIST_REFETCH)
      setRefreshButton(false);
  }, [
    tradeState,
    selectedSortOption,
    activeNetwork,
    previewCriteria,
    pageNumber
  ]);

  const handleRefresh = () => {
    setRefreshButton(false);
    const timeout = setTimeout(() => {
      refetchTradeList();
      mixpanel.track(MixpanelConfig.names.clickTradeListRefreshButton, {
        trade_state: TradeStateName[tradeState]
      });
    }, 300);
    return () => clearTimeout(timeout);
  };

  const showPagination = tradeListItems?.length > 0 && !isLoadingResults();

  return (
    <>
      <NavSecondary tradeState={tradeState} />
      <StyledPage className={className} variant={listVariant}>
        <StyledTradesContainer ref={tradeGridRef} variant={listVariant}>
          <StyledHeader variant={listVariant}>
            <TradeListHeader tradeState={tradeState} />
            <TradeFilterBar
              selectedSortOption={selectedSortOption}
              setSortOption={setSortOption}
              showSortOptions
              showLayoutToggle
              tradeState={tradeState}
              filtersButtonRef={filtersButtonRef}
            />
          </StyledHeader>
          <Filters
            tradeState={tradeState}
            userLists={userLists}
            companyId={user?.companyId}
            isEditing={isEditingFilters}
            isFilterReset={isFilterReset}
            setIsEditing={setIsEditingFilters}
            filtersButtonRef={filtersButtonRef}
            refetchFilters={refetch}
            setIsFilterReset={setIsFilterReset}
            filterPillName={filterPillName}
            isFiltersListFetched={isFiltersListFetched}
          />
          <StyledTradeGridContainer variant={listVariant}>
            {((isEditingFilters && previewCriteria?.criteria) ||
              activeList?.id !== FilterNames.all.id) &&
              !isLoadingResults() && (
                <StyledFilterIndicator>
                  <span>
                    {numberOfTrades}{' '}
                    <FormattedMessage id="list.filter.results" />
                  </span>{' '}
                  <span>{filterCountSubtitle(activeList?.id)}</span>
                  {activeList?.id !== FilterNames.all.id && (
                    <>
                      <span>&quot;{activeList?.title}&quot;</span>
                      <span> :</span>
                    </>
                  )}
                </StyledFilterIndicator>
              )}
            <FilterPills
              tradeState={tradeState}
              setIsFilterReset={setIsFilterReset}
              setFilterPillName={setFilterPillName}
            />
            {hasPreviousPage && showPagination && (
              <Pagination
                loadPrevPage={getPrevPage}
                loadNextPage={getNextPage}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                pageNumber={pageNumber}
              />
            )}
            <TradeGrid
              testId="tradeGrid"
              tradeSummaries={tradeListItems}
              gridTradeList={gridTradeListItems}
              saleEventSummaries={saleEventItems}
              tradeSelectionHandler={tradeSelectionHandler}
              variant={listVariant}
              selectedActiveList={activeList}
              tradeState={tradeState}
              isLoading={isLoadingResults()}
              pageNumber={pageNumber}
              onConfirmOpen={onConfirmOpen}
              pendingActiveProxy={pendingActiveProxy}
              onCurrentProxyUpdated={onCurrentProxyUpdated}
              confirmationMpTracking={confirmationMpTracking}
              bidModalRef={bidModalRef}
              activeNetwork={activeNetwork}
              hasNextPage={hasNextPage}
              showSaleEvent={showSaleEvent}
            />
            {(hasNextPage || hasPreviousPage) && showPagination && (
              <Pagination
                loadPrevPage={getPrevPage}
                loadNextPage={getNextPage}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                pageNumber={pageNumber}
              />
            )}
            {!hasNextPage && !isLoadingResults() && (
              <TradeListFooter
                tradeState={tradeState}
                previewCriteria={previewCriteria}
                activeList={activeList}
                activeNetwork={activeNetwork}
                showFooterLabel={tradeListItems?.length > 0}
                numberOfTrades={numberOfTrades}
                isEditingFilters={isEditingFilters}
                setIsEditingFilters={setIsEditingFilters}
              />
            )}
          </StyledTradeGridContainer>
          {listVariant === ListVariant.GRID && <MiniVdp />}
        </StyledTradesContainer>
        <ConfirmModal
          isOpen={isConfirmationOpen}
          modalLogic={confirmModalLogic}
          isLoading={isConfirmLoading}
          loadingMsg={<FormattedMessage id="modal.confirmation.loading" />}
        />
        <BidModal
          ref={bidModalRef}
          onCurrentProxyUpdated={onCurrentProxyUpdated}
          location={listVariant}
        />
      </StyledPage>
      <StyledRefreshButtonContainer
        onClick={handleRefresh}
        showButton={refreshButton}
      >
        {tradeState === TradeState.active && (
          <FormattedMessage id="list.button.refresh.text.active" />
        )}
        {tradeState === TradeState.upcoming && (
          <FormattedMessage id="list.button.refresh.text.upcoming" />
        )}
        <StyledRefreshButton>
          <FormattedMessage id="list.button.refresh" />
        </StyledRefreshButton>
      </StyledRefreshButtonContainer>
    </>
  );
};

export default Buying;
